import { Button } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../../analytics/utils/firebaseUtils'
import useUser from '../../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import { backendCall } from '../../../../modules/backendApi'
import { trpc } from '../../../../modules/trpc'
import ObjectBasicQuestions, {
	ObjectInformation,
} from '../../../../onboarding/questionParts/ObjectBasicQuestions'
import PageWrapper from '../../../../wrappers/pageWrapper/PageWrapper'

const MeteringPointInformation: React.FC = () => {
	const utils = trpc.useContext()

	const { user } = useUser()

	const object = useContext(ObjectContext)

	const preferenceInitialState = useMemo(() => {
		return {
			heatingType: object?.heatingType,
			squares: object?.squares,
			type: object?.type,
			personCount: object?.personCount,
			heatPump: object?.heatPump,
			underfloorHeating: object?.underfloorHeating,
			fireplace: object?.fireplace,
			consumptionElements:
				object.consumptionElements.map(element => ({
					name: element.name,
					type: element.type,
				})) ?? [],
		}
	}, [object])

	useEffect(() => {
		setPreferenceOptions(preferenceInitialState)
	}, [preferenceInitialState])

	const [preferenceOptions, setPreferenceOptions] =
		useState<ObjectInformation>(preferenceInitialState)

	const queryClient = useQueryClient()

	const onSubmit = async () => {
		logEvent('click-object-basics-save')

		const editingObject = object

		if (!editingObject.id || !user) return

		const elementsToDelete = editingObject.consumptionElements.filter(
			element => !preferenceOptions.consumptionElements.some(e => e.name === element.name)
		)

		const elementsToAdd = preferenceOptions.consumptionElements.filter(
			element => !editingObject.consumptionElements.some(e => e.name === element.name)
		)

		const objectUpdates: Omit<ObjectInformation, 'consumptionElements'> = {
			heatingType: preferenceOptions.heatingType,
			squares: preferenceOptions.squares,
			type: preferenceOptions.type,
			personCount: preferenceOptions.personCount,
			heatPump: preferenceOptions.heatPump,
			underfloorHeating: preferenceOptions.underfloorHeating,
			fireplace: preferenceOptions.fireplace,
		}

		await Promise.all([
			...elementsToDelete.map(element => backendCall.delete('consumptionElements', element.id)),
			...elementsToAdd.map(element =>
				backendCall.add('consumptionElements', {
					type: element.type,
					name: element.name,
					objectId: editingObject.id,
					custom: false,
				})
			),

			backendCall.edit('objects', editingObject.id, objectUpdates),
		])

		queryClient.invalidateQueries('objectOptions' as any)
		queryClient.invalidateQueries('objectConsumptionElements' as any)

		utils.objects.get.invalidate({ objectId: editingObject.id })
	}

	const { t: tc } = useTranslation('common')

	const saveButton = (disabled: boolean) => (
		<div className='mb-6'>
			<Button onClick={onSubmit} disabled={disabled} className='m-0'>
				{tc('tallenna', { ns: 'common' })}
			</Button>
		</div>
	)

	return (
		<PageWrapper page='meteringPointInformation' appBarOptions={{ navigationType: 'back' }}>
			<ObjectBasicQuestions
				options={preferenceOptions}
				setOptions={setPreferenceOptions}
				initialState={preferenceInitialState}
				saveButton={saveButton}
			/>
		</PageWrapper>
	)
}

export default MeteringPointInformation
